import { css } from "@emotion/react";
import { usePermissions } from "@podtrac/common/accounts";
import { Loading } from "@podtrac/common/components/Loading";
import { DateTime } from "luxon";
import React, { useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { FaQuestionCircle } from "react-icons/fa";
import Modal from "react-modal";
import { Link, useParams } from "react-router-dom";
import defaultIcon from "../../assets/default-icon.png";
import { AsOf } from "../shared/AsOf";
import DetailedChart from "../shared/DetailedChart";
import { DownloadsLabel } from "../shared/DownloadsLabel";
import ExportButton from "../shared/ExportButton";
import {
    AllTimeDownloadsHelp,
    AverageDownloadsPerEpisodeHelp,
    KeyMetricsHelp,
    LatestEpisodeHelp,
    ThirtyDayAudienceHelp,
    ThirtyDayDownloadsHelp
} from "../shared/MetricsHelp";
import NoMeasurement from "../shared/NoMeasurement";
import PaidFeatureButton from "../shared/PaidFeatureButton";
import Placeholder from "../shared/Placeholder";
import SparkineChart from "../shared/SparlkineChart";
import { useAccount, useAccountID } from "../store/account";
import { useShow } from "../store/show";
import { useShows, useShowsPages } from "../store/shows";
import { ShowsCardView } from "./ShowsCardView";
import { ShowsTableView } from "./ShowsTableView";
import { useAtom, useAtomValue } from "jotai";
import { sortAtom, viewAtom } from "../dashboard/Dashboard";
import { Toolbar } from "./Toolbar";

const numberFormat = new Intl.NumberFormat().format;
const percentFormat = new Intl.NumberFormat(undefined, { style: "percent", signDisplay: "always" })
    .format;

export default function KeyMetrics() {

    return (
        <>
            <Helmet>
                <title>Key Metrics - Podtrac</title>
            </Helmet>
            <AccountInfo />
            <Shows />
        </>
    );
}

const AccountName = ({ account }) => account?.accountName || null;
const LastMeasured = ({ account }) => (
    <div>{account ? <AsOf date={account.lastMeasured} /> : null}</div>
);

function AccountInfo() {
    const { pending: isLoading, data: account } = useAccount();
    const [help, setHelp] = useState(false);

    function openModal() {
        setHelp(true);
    }

    function closeModal() {
        setHelp(false);
    }

    return (
        <>
            <div
                css={css`
                    display: grid;
                    grid-column-gap: 1rem;
                    align-items: center;
                    grid-template-columns: 1fr auto;
                    & h1 {
                        margin-bottom: 0;
                    }
                `}
            >
                <h1>Key Metrics</h1>
                <div
                    onClick={openModal}
                    css={css`
                        display: inline;
                        font-size: 0.65rem;
                        line-height: 1rem;
                        color: var(--podtrac-blue-darker);
                        cursor: pointer;
                        text-transform: none;
                    `}
                >
                    about this report <FaQuestionCircle style={{ verticalAlign: "text-top" }} />
                </div>
                <Placeholder loading={isLoading} style={{ width: "8em" }}>
                    <LastMeasured account={account} />
                </Placeholder>
                {/*account && (account.isPaidPlan || account.offerUpgrade) ? (
                    <PaidFeatureButton
                        hasFeature={account.isPaidPlan}
                        dialogContent="Export of this report to an Excel file is a premium feature available in the Grow Your Show Plan."
                    >
                        {({ hasFeature, openModal }) => {
                            return (
                                <ExportButton
                                    onClick={
                                        hasFeature
                                            ? () =>
                                                  (window.location = `/__/${account.accountID}/reports/keymetricsexport`)
                                            : openModal
                                    }
                                />
                            );
                        }}
                    </PaidFeatureButton>
                ) : null*/}
            </div>

            <Modal
                isOpen={!!help}
                onRequestClose={closeModal}
                style={modalStyles}
                contentLabel="Key Metrics - About this Report"
            >
                <div
                    css={css`
                        display: flex;
                        align-items: center;
                        & > h1 {
                            margin: 0.5rem 0;
                            font-size: 1.5rem;
                            font-weight: 500;
                            color: #000;
                            & span {
                                opacity: 0.65;
                                display: inline-block;
                                font-size: 0.8em;
                                margin-left: 1em;
                                text-transform: uppercase;
                            }
                            & strong {
                                display: inline-block;
                                margin-left: 0.5em;
                            }
                        }
                        margin-bottom: 1rem;
                    `}
                >
                    <h1
                        css={css`
                            flex: 1;
                        `}
                    >
                        Key Metrics <span>About this Report</span>
                    </h1>
                    <div
                        onClick={closeModal}
                        css={css`
                            font-size: 1.5rem;
                            font-weight: 500;
                            color: #999;
                            cursor: pointer;
                            &:hover {
                                color: #000;
                            }
                        `}
                    >
                        ×
                    </div>
                </div>
                <div
                    css={css`
                        max-height: 70vh;
                        overflow: auto;
                        padding-right: 0.5rem;
                    `}
                >
                    <KeyMetricsHelp />
                </div>
            </Modal>
        </>
    );
}

function Shows() {
    const { accountID } = useParams();
    const view = useAtomValue(viewAtom);

    // check for any shows without filter
    const query =  useShows(accountID, "T", "", 0);
    const { data: shows, isLoading } = query;
    const { data, ...rest } = query;

    return <>
        {/*<pre>{JSON.stringify(rest, null, 2)}</pre>*/}
        {(isLoading ? <Loading /> : shows?.totalItems > 0 ?
            <>
                <Toolbar />
                {view == "card" ? <ShowsCardView /> : <ShowsTableView />}
            </> :
            <NoShows />
        )}
    </>;
}

function NoShows() {
    const { accountID } = useParams();
    const { data: permissions } = usePermissions(accountID);
    return <div style={{ backgroundColor: "#dff2fa", padding: "1rem", margin: "1rem 0" }}>
        <h2>Welcome to Podtrac</h2>

        {permissions?.canAddShow ? (
            <p>
                <Link to="add-show">Add a show</Link> to get started.
            </p>
        ) : null}
    </div>;
}

const pageSize = 20;
function ShowsOLD() {
    const accountID = useAccountID();
    const { data: account } = useAccount(accountID);
    const { data: permissions } = usePermissions(accountID);
    const { isLoading, data: { items: shows = [] } = {} } = useShows(accountID) || {};
    const activeShows = shows.filter(s => s.active);
    const [page, setPage] = useState(0);
    const pageCount =
        activeShows && activeShows.length > pageSize ? Math.ceil(activeShows.length / pageSize) : 0;
    const pages = Array.from({ length: pageCount }).map((_, i) => i);
    const pageClick = page => event => {
        event.preventDefault();
        setPage(page);
        document.querySelector("main")?.scrollTo(0, 0);
    };
    return (
        <>
            {/*<h2>Shows</h2>*/}
            {isLoading ? (
                <Loading left /> // TODO: a one-unit show placeholder
            ) : activeShows.length ? (
                activeShows
                    .filter((_, i) => i >= page * pageSize && i < page * pageSize + pageSize)
                    .map(s => (
                        <Show
                            key={s.showID}
                            accountID={accountID}
                            showID={s.showID}
                            showSummary={s}
                        />
                    ))
            ) : (
                <div style={{ backgroundColor: "#dff2fa", padding: "1rem", margin: "1rem 0" }}>
                    <h2>Welcome to Podtrac</h2>

                    {permissions?.canAddShow == "Admin" ? (
                        <p>
                            <Link to="add-show">Add a show</Link> to get started.
                        </p>
                    ) : null}
                </div>
            )}
            {pages.length ? (
                <div
                    css={css`
                        display: flex;
                        margin: 1rem;
                        justify-content: center;
                        & button {
                            min-width: 2rem;
                            height: 2rem;
                            margin: 0.1rem;
                        }
                    `}
                >
                    <button onClick={pageClick(page - 1)} disabled={page <= 0}>
                        ◄
                    </button>
                    {pages.map(p => (
                        <button key={p} onClick={pageClick(p)} disabled={p == page}>
                            {p + 1}
                        </button>
                    ))}
                    <button onClick={pageClick(page + 1)} disabled={page >= pageCount - 1}>
                        ►
                    </button>
                </div>
            ) : null}
            {/*<pre>{JSON.stringify({ pageCount, pages }, null, 2)}</pre>*/}
        </>
    );
}

const modalStyles = {
    content: {
        top: "10%",
        left: "10%",
        right: "10%",
        bottom: "auto"
        //marginRight: "-50%",
        //transform: "translate(-50%, -50%)"
    }
};

Modal.setAppElement("#root");
Modal.defaultStyles.overlay.backgroundColor = "#666666cc";

function Show({ accountID, showID, showSummary }) {
    const downloadsLabel = DownloadsLabel({ showID });

    const elemRef = useRef();

    const query = useShow(accountID, showID),
        { isPlaceholderData, isLoading, isError, data: show = {}, refetch } = query;

    const [modal, setModal] = React.useState();

    function openModal(name, range, value, prevValue, index, prevIndex, help) {
        console.log("openModal", { name, range, value, prevValue, index, prevIndex, help });
        setModal({ name, range, value, prevValue, index, prevIndex, help });
    }

    function closeModal() {
        setModal();
    }

    return (
        <div
            ref={elemRef}
            css={css`
                margin: 1rem 0 2rem;
                padding: 0.4375rem;
                border: 1px solid #ddd;
                page-break-inside: avoid;
                break-inside: avoid;
            `}
        >
            <div
                css={css`
                    display: grid;
                    grid-template-columns: min-content auto 20ch 10ch;
                    align-items: center;
                    h3 {
                        margin: 0 0 0 0.5rem;
                        font-size: 1.5rem;
                        line-height: 2rem;
                        font-weight: 500;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }
                    margin-bottom: 0.5rem;
                    padding-bottom: 0.4375rem;
                    border-bottom: 0.0625rem solid #eee;
                `}
            >
                <Link to={`shows/${showID}`}>
                    <img
                        src={showSummary.showImageUrl || defaultIcon}
                        css={css`
                            display: block;
                            width: 3rem;
                            height: 3rem;
                            max-width: 3rem;
                            border: 0.0625rem solid var(--border-color-low-contrast);
                        `}
                        onError={event => (event.target.src = defaultIcon)}
                    />
                </Link>
                <h3>
                    <Link
                        css={css`
                            text-decoration: none;
                            &:hover {
                                text-decoration: underline;
                            }
                        `}
                        to={`shows/${showID}`}
                    >
                        {showSummary.showName}
                    </Link>
                </h3>
                {show.hasMeasurement ? (
                    <>
                        <div
                            css={css`
                                display: flex;
                                flex-direction: column;
                                align-items: center;
                            `}
                        >
                            <div
                                css={css`
                                    font-size: 0.65rem;
                                    line-height: 1rem;
                                    text-transform: uppercase;
                                    font-weight: 400;
                                `}
                            >
                                All-Time <DownloadsLabel showID={showID} />{" "}
                                <FaQuestionCircle
                                    css={css`
                                        cursor: pointer;
                                        font-size: 0.6rem;
                                        vertical-align: text-top;
                                        opacity: 0.7;
                                        transition: opacity 0.1s;
                                        &:hover {
                                            opacity: 1;
                                        }
                                    `}
                                    onClick={() =>
                                        openModal(
                                            "Downloads",
                                            "All-Time",
                                            0,
                                            0,
                                            null,
                                            null,
                                            AllTimeDownloadsHelp
                                        )
                                    }
                                />
                            </div>
                            <div
                                css={css`
                                    font-size: 1.5rem;
                                    line-height: 2rem;
                                `}
                            >
                                {show.globalDownloadsAllTime !== undefined
                                    ? numberFormat(show.globalDownloadsAllTime)
                                    : "\u00A0"}
                            </div>
                        </div>
                        <Link
                            css={css`
                                text-align: right;
                                text-transform: uppercase;
                                font-weight: 600;
                                line-height: 2rem;
                                font-size: 1rem;
                                text-decoration: none;
                                &:hover {
                                    text-decoration: underline;
                                }
                            `}
                            to={`shows/${showID}`}
                        >
                            More ▶
                        </Link>
                    </>
                ) : null}
            </div>
            {showSummary.hasMeasurement ? (
                isLoading ? (
                    <Loading />
                ) : isError ? (
                    <p style={{ margin: "2em" }}>
                        Error loading show data{" "}
                        <button type="button" onClick={refetch}>
                            retry
                        </button>
                    </p>
                ) : (
                    <>
                        <div
                            css={css`
                                display: grid;
                                grid-template-columns: repeat(3, 1fr);
                                grid-gap: 0.5rem;
                            `}
                        >
                            <ChartedMetric
                                range="Average 7-Day"
                                name={downloadsLabel + "/Episode"}
                                value={show.averageSevenDayGlobalDownloads}
                                prevValue={show.averageSevenDayGlobalDownloadsPrevious}
                                index={"averageSevenDayGlobalDownloads"}
                                prevIndex={"averageSevenDayGlobalDownloadsPrevious"}
                                dateIndex={"measurementDate"}
                                data={show.sparklines}
                                openModal={openModal}
                                help={AverageDownloadsPerEpisodeHelp}
                            />
                            <ChartedMetric
                                range="30-Day"
                                name={downloadsLabel}
                                value={show.globalDownloads30Day}
                                prevValue={show.globalDownloads30DayPrevious}
                                index={"globalDownloads30Day"}
                                prevIndex={"globalDownloads30DayPrevious"}
                                dateIndex={"measurementDate"}
                                data={show.sparklines}
                                openModal={openModal}
                                help={ThirtyDayDownloadsHelp}
                            />
                            <ChartedMetric
                                range="30-Day"
                                name="Audience"
                                value={show.globalAudience30Day}
                                prevValue={show.globalAudience30DayPrevious}
                                index={"globalAudience30Day"}
                                prevIndex={"globalAudience30DayPrevious"}
                                dateIndex={"measurementDate"}
                                data={show.sparklines}
                                openModal={openModal}
                                help={ThirtyDayAudienceHelp}
                            />
                        </div>
                        <LatestEpisode show={show} openModal={openModal} />
                        <MetricModal show={show} modal={modal} closeModal={closeModal} />
                    </>
                )
            ) : (
                <NoMeasurement show={showSummary} />
            )}

            {/*<pre style={{ background: "orange", padding: 20 }}>
                {JSON.stringify({ showSummary, query }, null, 2)}
            </pre>*/}
        </div>
    );
}

function MetricModal({ show: s, modal, closeModal }) {
    const { help: Help } = modal || {};
    const chartName = modal
        ? /Episode$/.test(modal.name)
            ? "Avg 7-day dl/ep"
            : `${modal.range} ${modal.name}`
        : "";
    console.log("MetricModal", { s, modal, closeModal });
    return (
        <Modal
            isOpen={!!modal}
            onRequestClose={closeModal}
            style={modalStyles}
            contentLabel={s.showName}
        >
            {modal && (
                <>
                    <div
                        css={css`
                            display: flex;
                            align-items: center;
                            & > h1 {
                                margin: 0 0 0 0.5rem;
                                font-size: 1.5rem;
                                font-weight: 500;
                                color: #000;
                                & span {
                                    opacity: 0.65;
                                    display: inline-block;
                                    font-size: 0.8em;
                                    margin-left: 1em;
                                    text-transform: uppercase;
                                }
                                & strong {
                                    display: inline-block;
                                    margin-left: 0.5em;
                                }
                            }
                            margin-bottom: 1rem;
                        `}
                    >
                        <img
                            src={s.showImageUrl || defaultIcon}
                            css={css`
                                width: 32px;
                                border: 1px solid var(--border-color-low-contrast);
                            `}
                        />
                        <h1
                            css={css`
                                flex: 1;
                            `}
                        >
                            {s.showName}{" "}
                            <span>
                                {modal.range} <strong>{modal.name}</strong>
                            </span>
                        </h1>
                        <div
                            onClick={closeModal}
                            css={css`
                                font-size: 1.5rem;
                                font-weight: 500;
                                color: #999;
                                cursor: pointer;
                                &:hover {
                                    color: #000;
                                }
                            `}
                        >
                            ×
                        </div>
                    </div>

                    {modal.index ? (
                        <DetailedChart
                            data={s.sparklines}
                            index={modal.index}
                            prevIndex={modal.prevIndex}
                            dateIndex={"measurementDate"}
                            name={chartName}
                        />
                    ) : null}
                    <div
                        css={css`
                            width: 75vw;
                            max-height: 60vh;
                            overflow: auto;
                            font-size: 0.9rem;
                            padding-right: 0.5rem;
                            & > h2 {
                                text-transform: none;
                                margin: 0.5rem 0 1rem;
                                color: initial;
                                font-weight: 500;
                            }
                        `}
                    >
                        <Help />
                    </div>
                </>
            )}
        </Modal>
    );
}

function ChartedMetric({
    name,
    range,
    value,
    prevValue,
    data,
    dateIndex,
    index,
    prevIndex,
    help,
    openModal
}) {
    return (
        <div
            onClick={() => openModal(name, range, value, prevValue, index, prevIndex, help)}
            css={css`
                display: grid;
                justify-items: center;
                cursor: pointer;
                padding: 0.25rem;
                border-left: 0.0625rem solid transparent;
                border-right: 0.0625rem solid transparent;
                transition: all 0.1s;
                &:hover {
                    box-shadow: 0 0 0.125rem #eee;
                }
            `}
        >
            <div
                css={css`
                    font-size: 0.65rem;
                    line-height: 1rem;
                    text-transform: uppercase;
                    font-weight: 400;
                `}
            >
                {range}
            </div>
            <div
                css={css`
                    line-height: 1.5rem;
                    font-size: 1.25rem;
                    font-weight: 700;
                `}
            >
                {name}{" "}
                <FaQuestionCircle
                    css={css`
                        cursor: pointer;
                        opacity: 0.7;
                        transition: opacity 0.1s;
                        font-size: 0.65rem;
                        vertical-align: text-top;
                        &:hover {
                            opacity: 1;
                        }
                    `}
                />
            </div>
            <div
                css={css`
                    font-size: 2rem;
                    line-height: 3rem;
                `}
            >
                {value !== undefined ? numberFormat(value) : "\u00A0"}
            </div>
            <SparkineChart data={data} index={index} dateIndex={dateIndex} />
            <div
                css={css`
                    font-size: 1rem;
                    line-height: 1.5rem;
                `}
            >
                <GrowthSpan value={value} prevValue={prevValue}>
                    month/month
                </GrowthSpan>
            </div>
        </div>
    );
}

function LatestEpisode({ show: s, openModal }) {
    return (
        <div
            css={css`
                display: grid;
                grid-template-columns: 1fr 20ch 10ch;
                margin-top: 0.5rem;
                padding-top: 0.1875rem;
                border-top: 0.0625rem solid #eee;
                align-items: center;
            `}
        >
            <div
                css={css`
                    font-size: 0.65rem;
                    line-height: 1rem;
                    text-transform: uppercase;
                    font-weight: 400;
                `}
            >
                Latest Episode
            </div>
            <div
                css={css`
                    font-size: 0.65rem;
                    line-height: 1rem;
                    text-transform: uppercase;
                    font-weight: 400;
                    justify-self: center;
                `}
            >
                <DownloadsLabel showID={s.showID} /> To-Date{" "}
                <FaQuestionCircle
                    css={css`
                        cursor: pointer;
                        opacity: 0.7;
                        transition: opacity 0.1s;
                        vertical-align: text-top;
                        &:hover {
                            opacity: 1;
                        }
                    `}
                    onClick={() =>
                        openModal(
                            "Downloads-To-Date",
                            "Latest Episode",
                            0,
                            0,
                            null,
                            null,
                            LatestEpisodeHelp
                        )
                    }
                />
            </div>
            <div
                css={css`
                    font-size: 0.65rem;
                    line-height: 1rem;
                    text-transform: uppercase;
                    font-weight: 400;
                    justify-self: center;
                `}
            >
                vs. Previous
            </div>
            <div
                css={css`
                    font-size: 1.25rem;
                    line-height: 1.5rem;
                    align-self: center;
                    font-weight: 500;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                `}
            >
                {s.currentEpisodeTitle}
                <div
                    css={css`
                        font-size: 0.65rem;
                        line-height: 1rem;
                        text-transform: uppercase;
                        font-weight: 400;
                        opacity: 0.5;
                    `}
                >
                    Published:{" "}
                    {s.currentEpisodePubDate &&
                        DateTime.fromISO(s.currentEpisodePubDate, { zone: "utc" }).toLocaleString(
                            DateTime.DATE_FULL
                        )}
                </div>
            </div>
            <div
                css={css`
                    font-size: 1.75rem;
                    line-height: 2.5rem;
                    justify-self: center;
                `}
            >
                {s.currentEpisodeGlobalDownloads !== undefined
                    ? numberFormat(s.currentEpisodeGlobalDownloads)
                    : "\u00A0"}
            </div>
            <div
                css={css`
                    font-size: 1.25rem;
                    line-height: 2.5rem;
                    justify-self: center;
                    align-self: center;
                `}
            >
                <GrowthSpan
                    value={s.currentEpisodeGlobalDownloads}
                    prevValue={s.previousEpisodeGlobalDownloadsOverTimePeriod}
                />
            </div>
        </div>
    );
}

const epsilon = 0.01;

function GrowthSpan({ value, prevValue, children }) {
    if (!prevValue) return "\u00A0";

    let growth = (value - prevValue) / prevValue;
    if (Math.abs(growth) < epsilon) growth = 0;

    const msg = percentFormat(growth);

    return (
        <span
            css={css`
                color: ${growth > 0 ? "#0a0" : "#888"};
                font-weight: 500;
            `}
        >
            {msg} {children}
        </span>
    );
}
